import { Fragment, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { getStoredUser, setStoredUser, clearStoredUser } from '../lib/auth'
import {
  getStoredProfile, getRawStoredProfile, setStoredProfile,
  clearStoredProfile,
  dismissProfileSetup, clearProfileSetupDismissed,
  isOnboardingComplete,
} from '../lib/userProfile'
import { Capacitor } from '@capacitor/core'
import { useTheme } from '../ThemeContext'
import { loginWithKakao as _loginKakao, loginWithKakaoNative, logoutFromKakao } from '../lib/kakaoAuth'
import { loginWithApple as _loginApple } from '../lib/appleAuth'
import { loginWithGoogle as _loginGoogle, logoutFromGoogle } from '../lib/googleAuth'
import { signOutFirebase, subscribeAuthState } from '../lib/firebaseAuth'
import { auth } from '../lib/firebase'
import {
  saveProfile as saveProfileToCloud,
  saveGrade as saveGradeToCloud,
  saveStudentContext as saveStudentContextToCloud,
  getProfile as fetchProfileFromCloud,
  completeOnboarding as completeOnboardingCloud,
  skipOnboarding as skipOnboardingCloud,
  upsertUserDoc,
} from '../services/userDataService'
import {
  resetProfileAvatar,
  restoreSocialProfileAvatar,
  saveSugiProfileAvatar,
} from '../services/profileAvatarService'
import { runPostLoginMigration } from '../lib/migration'
import { startShuttleFavoriteSync } from '../services/shuttleFavoriteSync'
import { startSavedNoticesSync } from '../services/noticeSavedSync'
import { deleteCurrentAccount } from '../services/accountDeletionService'
import { unregisterCurrentDeviceToken } from '../lib/pushRegistration'
import { startNotificationStateTracking } from '../services/notificationStateService'
import { cancelClassNotifications } from '../services/localClassNotifications'
import { cancelPersonalEventNotifications } from '../services/personalEventNotificationService'
import { setAnalyticsUserId, trackEvent } from '../lib/analytics'
import {
  clearDeletedAccountLocalData,
  clearUserLocalData,
  clearUserSessionData,
} from '../services/accountLocalData'
import LoginModal from '../components/auth/LoginModal'
import ProfileSetupModal from '../components/auth/ProfileSetupModal'
import AppStatusScreen from '../components/onboarding/AppStatusScreen'
import OnboardingScreen from '../components/onboarding/OnboardingScreen'

const AuthContext = createContext(null)

// App.css .app-container와 동일한 폭 — 인증/프로필 확인 중 보여주는 빈 오버레이도
// PC에서 앱 프레임 폭으로 클램프해 App이 뒤이어 나타날 때 크기가 튀지 않게 한다.
const APP_FRAME_MAX_WIDTH = 430

export function AuthProvider({ children }) {
  const { isDark } = useTheme()
  const [user,             setUser]         = useState(() => getStoredUser())
  const [profile,          setProfileState] = useState(() => getRawStoredProfile())
  const [modalOpen,        setModal]        = useState(false)
  const [profileSetupOpen, setProfileSetup] = useState(false)
  // A complete cached session can render immediately while Firebase Auth and
  // Firestore reconcile in the background.
  const initialUser = getStoredUser()
  const initialProfile = getRawStoredProfile()
  const hasLocalSession = Boolean(
    initialUser?.id &&
    initialProfile &&
    isOnboardingComplete(initialProfile)
  )
  // Firebase Auth 초기 세션 복원 중 loading: true → 완료 후 false
  // 새로고침 시 로그인 버튼 깜빡임 방지
  const [loading, setLoading] = useState(() => {
    if (!auth) return false
    if (hasLocalSession) return false
    return true
  })

  // ── 온보딩 게이트 상태 ───────────────────────────────────────────────────
  // profileChecked: 현재 firebaseUser에 대해 Firestore 프로필 조회 시도가 끝났는가
  // profileLoadError: 그 조회가 실패했고, 완료 여부를 판단할 로컬 캐시도 없는가
  const [profileChecked,   setProfileChecked]   = useState(() => !auth || hasLocalSession)
  const [profileLoadError, setProfileLoadError] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)
  const [sessionGeneration, setSessionGeneration] = useState(0)

  useEffect(() => {
    const uid = user?.uid ?? user?.id ?? null
    return uid ? startNotificationStateTracking(uid) : undefined
  }, [user?.uid, user?.id])

  // 셔틀 즐겨찾기: 로그인 계정 기준으로 클라우드와 동기화(앱 업데이트 후에도 유지)
  useEffect(() => {
    const uid = user?.uid ?? user?.id ?? null
    return uid ? startShuttleFavoriteSync(uid) : undefined
  }, [user?.uid, user?.id])

  // 공지 즐겨찾기: 셔틀과 같은 방식으로 클라우드와 동기화
  useEffect(() => {
    const uid = user?.uid ?? user?.id ?? null
    return uid ? startSavedNoticesSync(uid) : undefined
  }, [user?.uid, user?.id])

  const pendingRef = useRef(null)
  const logoutPendingRef = useRef(false)
  // 로그인/로그아웃 경계를 넘은 뒤 늦게 도착한 Firestore 프로필 응답을 폐기한다.
  const authSessionVersionRef = useRef(0)

  const isLoggedIn = !!user

  // ── Firebase Auth 세션 복원 + Firestore 프로필 동기화 ──────────────────────
  useEffect(() => {
    const unsub = subscribeAuthState(async (firebaseUser) => {
      const sessionVersion = authSessionVersionRef.current
      if (firebaseUser) {
        // GA4 User-ID uses Firebase's opaque UID only. This runs as soon as
        // Firebase restores or establishes a session, preserving anonymous
        // events before login while attributing subsequent events consistently
        // across web and native Firebase Analytics.
        setAnalyticsUserId(firebaseUser.uid)
        const uid = firebaseUser.uid
        const stored = getStoredUser()
        // 프로필 캐시는 legacy 단일 키지만, Firebase가 확인한 UID와 현재 세션 키가
        // 일치할 때만 먼저 쓴다. 다른 계정으로 전환된 경우에는 원격 응답 전까지
        // 이전 프로필을 state에 남기지 않는다.
        const cachedProfile = stored?.id === uid || stored?.uid === uid
          ? getRawStoredProfile()
          : null
        // Do not reintroduce a startup gate when this authenticated account
        // already has a completed local profile. Incomplete or absent caches
        // still follow the normal remote confirmation path.
        if (!cachedProfile || !isOnboardingComplete(cachedProfile)) {
          setProfileChecked(false)
          setProfileLoadError(false)
        }
        // localStorage가 없거나 오래된 UID를 들고 있으면 Firebase Auth를 기준으로 즉시 정렬한다.
        // 관리자 판별은 user.uid/auth.currentUser.uid가 일치해야 하므로 초기 복원 타이밍에서 특히 중요하다.
        const fbProviderId = firebaseUser.providerData?.[0]?.providerId ?? ''
        const provider = uid.startsWith('kakao:')
          ? 'kakao'
          : fbProviderId === 'apple.com'
            ? 'apple'
            : fbProviderId === 'google.com'
              ? 'google'
              : stored?.provider || 'unknown'
        const restoredUser = {
          ...(stored ?? {}),
          id:          uid,
          uid,          // uid 필드 명시 — 관리자 판별 등에서 user.uid 사용
          provider,
          displayName: firebaseUser.displayName || stored?.displayName || '사용자',
          email:       firebaseUser.email       || stored?.email       || null,
          photoURL:    firebaseUser.photoURL    || stored?.photoURL    || null,
        }
        if (!stored || stored.uid !== uid || stored.id !== uid) {
          setStoredUser(restoredUser)
        }
        setUser(restoredUser)

        if (cachedProfile) {
          setProfileState(cachedProfile)
          // UID가 확인된 캐시 프로필은 즉시 사용하고, Firestore는 아래에서 최신값을
          // 조용히 갱신한다. 이 경우 profile-loading 전면 오버레이를 유지하지 않는다.
          setProfileChecked(true)
          setLoading(false)
        } else {
          setProfileState(null)
        }

        // Firestore에서 최신 프로필 가져와 state + localStorage 캐시 동기화
        // → 어느 기기에서 로그인해도 Firestore 기준값으로 통일
        try {
          const fsProfile = await fetchProfileFromCloud(uid)
          if (sessionVersion !== authSessionVersionRef.current) return
          if (fsProfile) {
            setStoredProfile(fsProfile)           // localStorage 캐시 갱신
            setProfileState(fsProfile)             // 닉네임 없이 건너뛴 완료 상태도 유지
          }
        } catch (err) {
          if (sessionVersion !== authSessionVersionRef.current) return
          // Firestore 읽기 실패 → localStorage fallback 유지 (앱 동작 보장).
          // 캐시된 프로필로도 완료 여부를 판단할 수 없을 때만 에러 상태로 표시한다.
          console.warn('[Auth] 프로필 조회 실패:', err?.code ?? err?.message)
          const cached = getStoredProfile()
          if (!cachedProfile && !cached) {
            setProfileLoadError(true)
          }
        } finally {
          if (sessionVersion === authSessionVersionRef.current) setProfileChecked(true)
        }
      } else if (auth) {
        setAnalyticsUserId(null)
        // Firebase Auth가 canonical source이므로 provider 값과 무관하게
        // signed-out 이벤트를 앱/로컬 세션에 반영한다.
        clearStoredUser()
        setUser(null)
        setProfileState(null)
        setProfileSetup(false)
        pendingRef.current = null
        // 게스트(또는 방금 로그아웃한 사용자) → 온보딩 대상이 아니므로 확인 완료 처리
        setProfileChecked(true)
        setProfileLoadError(false)
      }
      setLoading(false)  // 첫 onAuthStateChanged 이후 로딩 완료
    })
    return unsub
  }, [])

  // ── 온보딩 게이트 판단 ───────────────────────────────────────────────────
  // Guests and completed local profiles may render without waiting for remote
  // refresh. Profiles that are absent or incomplete retain the normal gate.
  const onboardingStatus = !auth
    ? 'complete'                                   // Firebase 미설정(로컬 개발) — 게이트 비활성
    : !user
      ? 'complete'                                  // 게스트는 온보딩 대상 아님
      : isOnboardingComplete(profile)
        ? 'complete'
        : loading
          ? 'auth-loading'
          : !profileChecked
            ? 'profile-loading'
            : (profileLoadError && !isOnboardingComplete(profile))
              ? 'profile-load-error'
              : !isOnboardingComplete(profile)
                ? 'required'
                : 'complete'

  const retryProfileCheck = useCallback(async () => {
    const currentUser = getStoredUser()
    if (!currentUser?.id) return
    setProfileChecked(false)
    setProfileLoadError(false)
    try {
      const fsProfile = await fetchProfileFromCloud(currentUser.id)
      if (fsProfile) {
        setStoredProfile(fsProfile)
        setProfileState(fsProfile)
      }
    } catch (err) {
      console.warn('[Auth] 프로필 재조회 실패:', err?.code ?? err?.message)
      if (!getStoredProfile()) setProfileLoadError(true)
    } finally {
      setProfileChecked(true)
    }
  }, [])

  // 온보딩 완료 저장 — 성공해야만 로컬 상태/캐시를 갱신한다 (실패 시 입력값과
  // 완료 미확정 상태를 그대로 유지해 OnboardingScreen이 오류를 보여줄 수 있게 한다).
  const completeOnboarding = useCallback(async (data) => {
    const currentUser = getStoredUser()
    if (!currentUser?.id) throw new Error('로그인이 필요해요. 다시 로그인해 주세요.')
    const nickTrimmed = (data?.nickname ?? '').trim()
    if (!nickTrimmed || nickTrimmed.length > 10) {
      throw new Error('닉네임을 1~10자로 입력해 주세요.')
    }
    const saved = await completeOnboardingCloud(currentUser.id, {
      nickname: nickTrimmed,
      college: (data?.college ?? '').trim(),
      department: (data?.department ?? '').trim(),
      grade: data?.grade ?? '',
      entranceYear: data?.entranceYear ?? '',
    })
    setProfileState(saved)
    clearProfileSetupDismissed()
    setProfileSetup(false)
    setProfileLoadError(false)
    return saved
  }, [])

  const skipOnboarding = useCallback(async () => {
    const currentUser = getStoredUser()
    if (!currentUser?.id) throw new Error('로그인이 필요해요. 다시 로그인해 주세요.')
    // saved는 기존 닉네임·학과를 보존한 채 onboardingCompleted:true만 세운 프로필.
    // Firestore 저장이 끝난 뒤에만 로컬 상태로 반영해, 저장 완료 전 화면이 홈으로
    // 넘어가 상태가 되돌아가는 일이 없게 한다(setProfileState 이후 게이트 재평가).
    const saved = await skipOnboardingCloud(currentUser.id)
    setProfileState(saved)
    clearProfileSetupDismissed()
    setProfileSetup(false)
    setProfileLoadError(false)
    if (import.meta.env.DEV) console.log('[Onboarding] AuthContext 상태 갱신 — onboardingCompleted =', saved?.onboardingCompleted,
      '→ 게이트 재평가로 홈 1회 전환 (로그인 세션 유지)')
    return saved
  }, [])

  // 개발 빌드에서만 기존 계정의 온보딩 게이트를 다시 연다. 프로필 자체나
  // 시간표·즐겨찾기 캐시는 삭제하지 않으며, 완료 플래그의 로컬 캐시만 Firestore와
  // 같은 false로 갱신한 뒤 즉시 게이트를 재평가한다.
  const resetOnboardingForDevelopment = useCallback(async () => {
    if (!(import.meta.env.MODE === 'development' || import.meta.env.MODE === 'ios-dev')) return null
    const currentUser = getStoredUser()
    if (!currentUser?.id) throw new Error('로그인한 계정에서만 온보딩을 다시 볼 수 있어요.')
    const { resetOnboardingForDevelopment: resetCloud } = await import('../services/devOnboardingService')
    const saved = await resetCloud(currentUser.id, getRawStoredProfile() ?? profile ?? {})
    setProfileState(saved)
    clearProfileSetupDismissed()
    setProfileSetup(false)
    setProfileLoadError(false)
    setProfileChecked(true)
    return saved
  }, [profile])

  // 웹/PWA: window.location redirect 트리거만 함. 실제 user 상태 업데이트는
  //   App.jsx KakaoCallbackScreen에서 처리 (페이지 이동하므로 이 Promise는 resolve 안 됨).
  // iOS 네이티브: Kakao SDK → 서버 검증 → Firebase Custom Token까지 끝내고 user를 resolve
  //   (Apple/Google과 동일한 패턴).
  const loginWithKakao = useCallback(async () => {
    if (Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform())) {
      const u = await loginWithKakaoNative()
      setStoredUser(u)
      setUser(u)
      trackEvent('login_complete', { provider: u?.provider || 'kakao' })
      if (u?.isNewUser) trackEvent('sign_up_complete', { provider: u.provider || 'kakao' })
      return u
    }
    return _loginKakao()  // 내부에서 window.location.href 이동 → 페이지 전환
  }, [])

  const loginWithApple = useCallback(async () => {
    const u = await _loginApple()
    clearProfileSetupDismissed()
    setStoredUser(u)
    setUser(u)
    trackEvent('login_complete', { provider: u?.provider || 'apple' })
    if (u?.isNewUser) trackEvent('sign_up_complete', { provider: u.provider || 'apple' })
    // Kakao와 동일하게: Firestore 유저 문서 생성/갱신 + localStorage 마이그레이션
    upsertUserDoc(u.uid, u)
    runPostLoginMigration(u.uid)
    return u
  }, [])

  const loginWithGoogle = useCallback(async () => {
    const u = await _loginGoogle()
    clearProfileSetupDismissed()
    setStoredUser(u)
    setUser(u)
    trackEvent('login_complete', { provider: u?.provider || 'google' })
    if (u?.isNewUser) trackEvent('sign_up_complete', { provider: u.provider || 'google' })
    upsertUserDoc(u.uid, u)
    runPostLoginMigration(u.uid)
    return u
  }, [])

  const logout = useCallback(async () => {
    if (logoutPendingRef.current) return false
    logoutPendingRef.current = true
    setLoggingOut(true)
    // await 중 auth-state/profile 요청이 완료돼도 이전 계정 state를 되살리지 못하게 한다.
    authSessionVersionRef.current += 1
    const currentUid = user?.uid ?? user?.id ?? auth?.currentUser?.uid ?? null
    try {
      // 이 기기의 FCM 토큰 문서를 먼저 제거(로그아웃한 계정으로 계속 푸시가 가지 않도록). 실패해도 로그아웃은 진행.
      // Independent cleanup: a remote-token or either local-kind failure must
      // never retain the authenticated session or block another cleanup.
      await Promise.allSettled([
        unregisterCurrentDeviceToken(currentUid),
        cancelClassNotifications(),
        cancelPersonalEventNotifications(),
      ])
      // Provider-side sessions are secondary. They must never prevent the
      // canonical Firebase sign-out from running.
      if (user?.provider === 'kakao')  await logoutFromKakao().catch(() => {})
      if (user?.provider === 'google') await logoutFromGoogle().catch(() => {})
      // Keep the opaque UID through this last account-scoped event, then clear
      // it before the anonymous session can emit product events.
      trackEvent('logout', { provider: user?.provider || 'unknown' })
      await signOutFirebase()
      setAnalyticsUserId(null)
      clearUserLocalData(currentUid)
      clearUserSessionData()
      clearStoredUser()
      pendingRef.current = null
      setUser(null)
      setProfileState(null)
      setModal(false)
      setProfileSetup(false)
      setProfileChecked(true)
      setProfileLoadError(false)
      // App와 모든 하위 Context/컴포넌트를 재마운트해 시간표·지도·즐겨찾기 메모리를 폐기한다.
      setSessionGeneration(generation => generation + 1)
      return true
    } finally {
      logoutPendingRef.current = false
      setLoggingOut(false)
    }
  }, [user])

  const deleteAccount = useCallback(async () => {
    const deleted = await deleteCurrentAccount()
    // 서버가 Auth와 Firestore를 모두 삭제한 뒤에만 브라우저/기기 세션을 정리한다.
    await signOutFirebase().catch(() => {})
    setAnalyticsUserId(null)
    clearDeletedAccountLocalData(deleted.uid)
    clearUserSessionData()
    authSessionVersionRef.current += 1
    setUser(null)
    setProfileState(null)
    setModal(false)
    setProfileSetup(false)
    setProfileChecked(true)
    setProfileLoadError(false)
    setSessionGeneration(generation => generation + 1)
    return deleted
  }, [])

  // patch만 Firestore에 보내고, 성공한 뒤에만 "현재 profile state + patch"로 로컬을 갱신한다.
  // getRawStoredProfile()로 만든 stale 전체 객체를 서버에 재전송하지 않는다 — 실패 시 throw가
  // 그대로 호출부로 전파돼 기존 profile state가 유지된다(성공을 가장하지 않는다).
  const saveProfile = useCallback(async (patch) => {
    const currentUser = getStoredUser()
    if (currentUser?.id) {
      await saveProfileToCloud(currentUser.id, patch)
    }
    const nextProfile = { ...(profile ?? {}), ...patch }
    setStoredProfile(nextProfile)
    setProfileState(nextProfile)
    setProfileSetup(false)
  }, [profile])

  // saveProfile과 달리 fire-and-forget이 아니다 — 실패를 그대로 throw해 호출부가
  // "저장 성공"을 가장하지 않고 실패 상태를 사용자에게 보여줄 수 있게 한다.
  // 로컬 캐시(profile state)도 서버 저장이 성공한 뒤에만 갱신한다.
  const saveGrade = useCallback(async (grade) => {
    const currentUser = getStoredUser()
    if (!currentUser?.id) throw new Error('로그인이 필요해요.')
    await saveGradeToCloud(currentUser.id, grade)
    const merged = { ...(getRawStoredProfile() ?? {}), grade }
    setStoredProfile(merged)
    setProfileState(getStoredProfile())
  }, [])

  // 시간표 시작 시 college/department/grade를 한 번에 확정 저장 — saveGrade와 동일 패턴(실패를
  // 그대로 throw). 기존 프로필 필드(닉네임 등)는 건드리지 않는다(merge 쓰기). saveProfile과
  // 동일하게 현재 profile state를 merge 기준으로 쓴다 — stale local(getRawStoredProfile())을
  // 기준으로 삼으면 로그인 사용자의 authoritative-null이 부활할 수 있다.
  const saveStudentContext = useCallback(async (college, department, grade) => {
    const currentUser = getStoredUser()
    if (!currentUser?.id) throw new Error('로그인이 필요해요.')
    await saveStudentContextToCloud(currentUser.id, { college, department, grade })
    const nextProfile = { ...(profile ?? {}), college, department, grade }
    setStoredProfile(nextProfile)
    setProfileState(nextProfile)
  }, [profile])

  const applyProfileAvatar = useCallback(({ profileAvatar, socialPhotoURL }) => {
    const merged = {
      ...(getRawStoredProfile() ?? {}),
      profileAvatar,
      ...(socialPhotoURL ? { socialPhotoURL } : {}),
    }
    setStoredProfile(merged)
    setProfileState(getStoredProfile())
  }, [])

  const selectSugiAvatar = useCallback(async (assetId) => {
    if (!user?.id) throw new Error('로그인이 필요합니다.')
    const result = await saveSugiProfileAvatar(user.id, assetId)
    applyProfileAvatar(result)
    return result.profileAvatar
  }, [applyProfileAvatar, user])

  const restoreSocialAvatar = useCallback(async () => {
    if (!user?.id) throw new Error('로그인이 필요합니다.')
    const result = await restoreSocialProfileAvatar(user.id, { currentUser: user, currentProfile: profile })
    applyProfileAvatar(result)
    setUser(prev => prev ? { ...prev, photoURL: result.socialPhotoURL ?? null } : prev)
    setStoredUser({ ...(getStoredUser() ?? user), photoURL: result.socialPhotoURL ?? null })
    return result.profileAvatar
  }, [applyProfileAvatar, profile, user])

  const restoreInitialAvatar = useCallback(async () => {
    if (!user?.id) throw new Error('로그인이 필요합니다.')
    const result = await resetProfileAvatar(user.id)
    const { profileAvatar } = result
    applyProfileAvatar(result)
    setUser(prev => prev ? { ...prev, photoURL: null } : prev)
    setStoredUser({ ...(getStoredUser() ?? user), photoURL: null })
    return profileAvatar
  }, [applyProfileAvatar, user])

  const clearProfile = useCallback(() => {
    clearStoredProfile()
    setProfileState(null)
  }, [])

  const openProfileSetup = useCallback(() => setProfileSetup(true), [])
  const openLoginModal   = useCallback(() => setModal(true), [])
  const closeLoginModal  = useCallback(() => {
    setModal(false)
    pendingRef.current = null
  }, [])

  const requireLogin = useCallback((cb) => {
    if (user) { cb?.(user); return }
    pendingRef.current = cb ?? null
    setModal(true)
  }, [user])

  function handleLoginSuccess(loggedInUser) {
    setModal(false)
    pendingRef.current?.(loggedInUser)
    pendingRef.current = null
  }
  function handleDismiss() {
    setModal(false)
    pendingRef.current?.(null)
    pendingRef.current = null
  }
  function handleProfileDismiss() {
    dismissProfileSetup()
    setProfileSetup(false)
  }

  // 'auth-loading'/'profile-loading' → 로딩 화면, 'profile-load-error' → 에러 화면,
  // 'required' → 온보딩 화면. 모두 App보다 위(zIndex 10000)에서 렌더링되어
  // 메인 화면이 먼저 보였다가 전환되는 플래시를 막는다.
  const blockingOverlay =
    onboardingStatus === 'auth-loading' || onboardingStatus === 'profile-loading'
      ? 'loading'
      : onboardingStatus === 'profile-load-error'
        ? 'error'
        : onboardingStatus === 'required'
          ? 'required'
          : null

  return (
    <AuthContext.Provider value={{
      user, isLoggedIn, profile, loading,
      loginWithKakao, loginWithApple, loginWithGoogle, logout, deleteAccount,
      requireLogin, openLoginModal, closeLoginModal,
      saveProfile, saveGrade, saveStudentContext, clearProfile, openProfileSetup,
      selectSugiAvatar, restoreSocialAvatar, restoreInitialAvatar,
      onboardingStatus, completeOnboarding, skipOnboarding, retryProfileCheck,
      ...((import.meta.env.MODE === 'development' || import.meta.env.MODE === 'ios-dev') ? { resetOnboardingForDevelopment } : {}),
    }}>
      <Fragment key={sessionGeneration}>{children}</Fragment>

      {modalOpen && (
        <LoginModal
          onLoginSuccess={handleLoginSuccess}
          onDismiss={handleDismiss}
          loginWithKakao={loginWithKakao}
          loginWithApple={loginWithApple}
          loginWithGoogle={loginWithGoogle}
        />
      )}

      {profileSetupOpen && (
        <ProfileSetupModal
          initialProfile={getRawStoredProfile()}
          onSave={saveProfile}
          onDismiss={handleProfileDismiss}
        />
      )}

      {blockingOverlay === 'loading' && (
        // 인증/프로필 확인 중 — 아이콘·스피너·문구 없이 앱 배경색만 채운 빈 오버레이.
        // 바깥(App.css .app-wrapper PC 배경)과 안쪽 프레임(.app-container 배경)을
        // 실제 앱과 동일한 두 톤으로 나눠 채워야, 확인이 끝나고 실제 앱이 나타날 때
        // PC에서 프레임 바깥 여백 색이 바뀌는 티가 나지 않는다.
        <div
          role="status"
          aria-label="불러오는 중"
          data-no-swipe-back=""
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            display: 'flex', justifyContent: 'center',
            overflow: 'hidden',
            background: isDark ? '#000000' : '#d8dce6',
          }}
        >
          <div style={{
            width: `min(100vw, ${APP_FRAME_MAX_WIDTH}px)`, height: '100dvh',
            background: isDark ? '#000000' : '#f5f5f7',
          }} />
        </div>
      )}
      {loggingOut && (
        <div
          role="status"
          aria-label="로그아웃 중"
          data-no-swipe-back=""
          style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            background: isDark ? '#000000' : '#f5f5f7',
          }}
        />
      )}
      {blockingOverlay === 'error' && <AppStatusScreen onRetry={retryProfileCheck} onLogout={logout} />}
      {blockingOverlay === 'required' && (
        <OnboardingScreen
          initialProfile={getRawStoredProfile()}
          onComplete={completeOnboarding}
          onSkip={skipOnboarding}
        />
      )}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
